/**
 * Schema Cache
 * 
 * In-memory cache for inferred node schemas
 */

import type { InferredNodeSchema, SchemaCache } from '@/types/inferred-schema';

class SchemaCacheManager {
  private cache: SchemaCache = {};
  private initialized = false;

  /**
   * Get schema from cache
   */
  get(nodeType: string): InferredNodeSchema | undefined {
    return this.cache[nodeType];
  }

  /**
   * Set schema in cache
   */
  set(nodeType: string, schema: InferredNodeSchema): void {
    this.cache[nodeType] = schema;
  }

  /**
   * Check if schema exists in cache
   */
  has(nodeType: string): boolean {
    return nodeType in this.cache;
  }

  /**
   * Clear entire cache
   */
  clear(): void {
    this.cache = {};
    this.initialized = false;
  }

  /**
   * Remove specific schema from cache
   */
  remove(nodeType: string): void {
    delete this.cache[nodeType];
  }

  /**
   * Get all cached schemas
   */
  getAll(): SchemaCache {
    return { ...this.cache };
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: Object.keys(this.cache).length,
      nodeTypes: Object.keys(this.cache),
      initialized: this.initialized,
    };
  }

  /**
   * Mark cache as initialized
   */
  markInitialized(): void {
    this.initialized = true;
  }

  /**
   * Check if cache is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Singleton instance
export const schemaCache = new SchemaCacheManager();
